import { useEffect, useState, useCallback, useRef } from 'react';
import { usePosts } from '@/hooks/usePosts';
import { FeedCard } from '@/components/FeedCard';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Search, X } from 'lucide-react';
import { PeopleDiscover, OnlineNow } from '@/components/community';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

// ── Section header ─────────────────────────────────────────────────────────
const SectionLabel = ({ children, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 6px' }}>
    <span style={{
      fontFamily: "'Azeret Mono', monospace",
      fontSize: '9px',
      letterSpacing: '3px',
      color: '#72788A',
      textTransform: 'uppercase',
    }}>{children}</span>
    {action}
  </div>
);

// ── New Member Crown card ──────────────────────────────────────────────────
function NewMemberCard({ user }) {
  const joinedDaysAgo = user.created_at
    ? Math.floor((Date.now() - new Date(user.created_at)) / 86400000)
    : null;

  return (
    <Link to={`/profile/${user.username}`} style={{ textDecoration: 'none' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 16px',
        transition: 'background .14s',
      }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        {/* Crown + avatar */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar style={{ width: '40px', height: '40px', border: '1.5px solid rgba(240,123,29,0.35)' }}>
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback style={{ background: 'linear-gradient(135deg,#F07B1D,#C85A12)', color: '#050608', fontFamily: "'Bebas Neue',sans-serif", fontSize: '14px' }}>
              {(user.name || user.username || '?').charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {/* Crown badge */}
          <span style={{
            position: 'absolute',
            top: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '11px',
            lineHeight: 1,
            filter: 'drop-shadow(0 1px 3px rgba(240,123,29,0.8))',
          }}>👑</span>
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: "'Instrument Sans',sans-serif", fontWeight: 600, fontSize: '14px', color: '#EAE0CC', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user.name || user.username}
          </p>
          <p style={{ fontFamily: "'Azeret Mono',monospace", fontSize: '10px', color: '#72788A', margin: '2px 0 0' }}>
            @{user.username}{joinedDaysAgo !== null && joinedDaysAgo <= 3 ? ' · just entered the chat' : joinedDaysAgo !== null && joinedDaysAgo <= 7 ? ' · new here' : ''}
          </p>
        </div>

        {/* Fire arrow */}
        <span style={{ fontSize: '12px', color: '#3A3F4E', flexShrink: 0 }}>→</span>
      </div>
    </Link>
  );
}

// ── HBCU filter pill ───────────────────────────────────────────────────────
const HBCU_SCHOOLS = [
  'Howard', 'Spelman', 'Morehouse', 'FAMU', 'Hampton',
  'Xavier', 'Tuskegee', 'Grambling', 'Prairie View', 'HBCU',
];

// ── Main Explore page ──────────────────────────────────────────────────────
export default function ExplorePage() {
  // Theme
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark')
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const borderClass = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';

  // Search
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const handleSearch = useCallback((q) => {
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setSearchResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/users/search', { params: { q, limit: 10 } });
        setSearchResults(res.data?.users || res.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  // New members ("Entered the Chat")
  const [newMembers, setNewMembers] = useState([]);
  const [hbcuFilter, setHbcuFilter] = useState(null);

  useEffect(() => {
    const fetchNew = async () => {
      try {
        const params = { limit: 8, sort: 'newest' };
        if (hbcuFilter) params.school = hbcuFilter;
        const res = await api.get('/users/discover', { params });
        const users = res.data?.users || res.data || [];
        // Sort by newest if possible
        setNewMembers(users.slice(0, 8));
      } catch {
        setNewMembers([]);
      }
    };
    fetchNew();
  }, [hbcuFilter]);

  // Trending feed mode (secondary)
  const [feedMode, setFeedMode] = useState(false);
  const { posts, loading: feedLoading, fetchExploreFeed } = usePosts();
  const scrollContainerRef = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadFeed = useCallback(async () => {
    await fetchExploreFeed(null, null);
  }, [fetchExploreFeed]);

  const loadMore = useCallback(async () => {
    if (loadingMore || posts.length === 0) return;
    const cursor = posts[posts.length - 1]?.created_at;
    if (!cursor) return;
    setLoadingMore(true);
    try { await fetchExploreFeed(cursor, null); }
    catch { }
    finally { setLoadingMore(false); }
  }, [posts, loadingMore, fetchExploreFeed]);

  useEffect(() => {
    if (!feedMode) return;
    loadFeed();
  }, [feedMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!feedMode) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < clientHeight * 2) loadMore();
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [feedMode, loadMore]);

  // ── Trending feed mode ──────────────────────────────────────────────────
  if (feedMode) {
    return (
      <div className="relative" data-testid="explore-page" style={{ height: 'var(--feed-card-height)' }}>
        {/* Header */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30 }}>
          <div style={{
            backdropFilter: 'blur(20px)',
            background: 'rgba(5,6,8,0.85)',
            borderBottom: `1px solid ${borderClass}`,
            display: 'flex',
            alignItems: 'center',
            padding: '10px 16px',
            gap: '10px',
          }}>
            <button onClick={() => setFeedMode(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#72788A', padding: '4px', display: 'flex', alignItems: 'center' }}>
              <X style={{ width: '16px', height: '16px' }} />
            </button>
            <span style={{ fontFamily: "'Azeret Mono',monospace", fontSize: '10px', letterSpacing: '3px', color: '#72788A', textTransform: 'uppercase' }}>
              Trending
            </span>
          </div>
        </div>

        {feedLoading && posts.length === 0 ? (
          <div className="feed-scroll-container">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="feed-card flex items-center justify-center" style={{ minHeight: 'var(--feed-card-height)' }}>
                <div className="space-y-4 w-full max-w-xs p-6">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2 flex-1"><Skeleton className="h-3 w-24" /><Skeleton className="h-3 w-16" /></div>
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div ref={scrollContainerRef} className="feed-scroll-container">
            {posts.map((post) => <FeedCard key={post.post_id} post={post} />)}
            {loadingMore && (
              <div className="feed-card flex items-center justify-center">
                <div className={cn("w-6 h-6 border-2 rounded-full animate-spin", isDark ? "border-white/20 border-t-white" : "border-gray-200 border-t-gray-700")} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Discovery mode (default) ────────────────────────────────────────────
  return (
    <div data-testid="explore-page" style={{ minHeight: '100%', paddingBottom: '80px' }}>

      {/* Sticky header + search */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        backdropFilter: 'blur(20px)',
        background: 'rgba(5,6,8,0.92)',
        borderBottom: `1px solid ${borderClass}`,
      }}>
        {/* Title row */}
        <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '22px',
            letterSpacing: '3px',
            color: '#EAE0CC',
          }}>Where Everybody At</span>
          <button
            onClick={() => setFeedMode(true)}
            style={{
              fontFamily: "'Azeret Mono',monospace",
              fontSize: '9px',
              letterSpacing: '2px',
              color: '#F07B1D',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'uppercase',
            }}
          >Trending ↗</button>
        </div>

        {/* Search bar */}
        <div style={{ padding: '0 16px 10px', position: 'relative' }}>
          <Search style={{ position: 'absolute', left: '28px', top: '50%', transform: 'translateY(-50%)', width: '14px', height: '14px', color: '#72788A', pointerEvents: 'none' }} />
          <input
            ref={searchRef}
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search people..."
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.05)',
              border: `1px solid ${borderClass}`,
              borderRadius: '8px',
              padding: '8px 36px',
              color: '#EAE0CC',
              fontFamily: "'Instrument Sans', sans-serif",
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {query && (
            <button
              onClick={() => { setQuery(''); setSearchResults([]); }}
              style={{ position: 'absolute', right: '28px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#72788A', display: 'flex', alignItems: 'center' }}
            >
              <X style={{ width: '14px', height: '14px' }} />
            </button>
          )}
        </div>
      </div>

      {/* ── Search Results ── */}
      {query && (
        <div style={{ paddingBottom: '8px' }}>
          {searching ? (
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div style={{ flex: 1 }}><Skeleton className="h-4 w-32 mb-1" /><Skeleton className="h-3 w-20" /></div>
                </div>
              ))}
            </div>
          ) : searchResults.length === 0 ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#72788A', fontFamily: "'Instrument Sans',sans-serif", fontSize: '14px' }}>
              No people found for "{query}"
            </div>
          ) : (
            searchResults.map((u) => (
              <Link key={u.user_id || u.username} to={`/profile/${u.username}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px' }}>
                <Avatar style={{ width: '40px', height: '40px' }}>
                  <AvatarImage src={u.picture} alt={u.name} />
                  <AvatarFallback style={{ background: 'rgba(255,255,255,0.08)', color: '#EAE0CC', fontFamily: "'Bebas Neue',sans-serif" }}>
                    {(u.name || u.username || '?').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: '#EAE0CC', fontWeight: 600, fontSize: '14px', margin: 0, fontFamily: "'Instrument Sans',sans-serif", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name || u.username}</p>
                  <p style={{ color: '#72788A', fontSize: '12px', fontFamily: "'Azeret Mono',monospace", margin: '2px 0 0' }}>@{u.username}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      )}

      {/* ── Discovery sections (hidden when searching) ── */}
      {!query && (
        <>
          {/* Online Now */}
          <div style={{ borderBottom: `1px solid ${borderClass}`, paddingBottom: '12px' }}>
            <SectionLabel>Online Now</SectionLabel>
            <div style={{ paddingLeft: '16px' }}>
              <OnlineNow limit={8} />
            </div>
          </div>

          {/* Entered the Chat — new members */}
          <div style={{ borderBottom: `1px solid ${borderClass}`, paddingBottom: '8px' }}>
            <SectionLabel>{'Entered the Chat 👑'}</SectionLabel>

            {/* HBCU school filter pills */}
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', padding: '0 16px 10px', scrollbarWidth: 'none' }}>
              <button
                onClick={() => setHbcuFilter(null)}
                style={{
                  flexShrink: 0,
                  fontFamily: "'Azeret Mono',monospace",
                  fontSize: '9px',
                  letterSpacing: '1px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  border: `1px solid ${hbcuFilter === null ? '#F07B1D' : 'rgba(255,255,255,0.07)'}`,
                  background: hbcuFilter === null ? 'rgba(240,123,29,0.12)' : 'transparent',
                  color: hbcuFilter === null ? '#F07B1D' : '#72788A',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >All</button>
              {HBCU_SCHOOLS.map(school => (
                <button
                  key={school}
                  onClick={() => setHbcuFilter(hbcuFilter === school ? null : school)}
                  style={{
                    flexShrink: 0,
                    fontFamily: "'Azeret Mono',monospace",
                    fontSize: '9px',
                    letterSpacing: '1px',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    border: `1px solid ${hbcuFilter === school ? '#F07B1D' : 'rgba(255,255,255,0.07)'}`,
                    background: hbcuFilter === school ? 'rgba(240,123,29,0.12)' : 'transparent',
                    color: hbcuFilter === school ? '#F07B1D' : '#72788A',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >{school}</button>
              ))}
            </div>

            {newMembers.length === 0 ? (
              <div style={{ padding: '8px 16px 4px' }}>
                <Skeleton className="h-12 w-full rounded mb-2" />
                <Skeleton className="h-12 w-full rounded" />
              </div>
            ) : (
              newMembers.map(u => <NewMemberCard key={u.user_id || u.username} user={u} />)
            )}
          </div>

          {/* Suggested By Your People */}
          <div>
            <SectionLabel>Suggested By Your People</SectionLabel>
            <div style={{ padding: '0 16px 8px' }}>
              <PeopleDiscover limit={6} fullWidth />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
